import React, { useState, useEffect } from "react";
import {
  SingleRoleRouletteState,
  SingleRoleRouletteAction,
  GameMode,
} from "./SingleRoleRouletteReducer";
import { useTranslation } from "react-i18next";

interface Props {
  state: SingleRoleRouletteState;
  dispatch: React.Dispatch<SingleRoleRouletteAction>;
  onCompleteSettings?: () => void;
  onStart?: () => void;
  onResetSettings?: () => void;
  onRestart?: () => void;
  error?: string;
  setError?: (error: string) => void;
}

const MAX_LABEL_LENGTH = 10;

const SingleRoleRouletteGameSetting: React.FC<Props> = ({
  state,
  dispatch,
  onCompleteSettings,
  onStart,
  onResetSettings,
  onRestart,
  error: propError,
  setError: propSetError,
}) => {
  const { t } = useTranslation();
  const [labelName, setLabelName] = useState("");
  const [labelCount, setLabelCount] = useState(1);
  const [isEditingCount, setIsEditingCount] = useState(false);
  const [defaultLabel, setDefaultLabel] = useState(
    state.defaultLabel?.name || ""
  );
  const [error, setError] = useState("");

  // Props로 받은 error와 setError가 있으면 그것을 사용
  const currentError = propError !== undefined ? propError : error;
  const currentSetError = propSetError || setError;

  // state.defaultLabel이 변경될 때 로컬 state 동기화
  useEffect(() => {
    setDefaultLabel(state.defaultLabel?.name || "");
  }, [state.defaultLabel?.name]);

  // 설정 완료 상태가 변경될 때 에러 메시지 지우기
  useEffect(() => {
    if (state.settingsCompleted) {
      setError("");
    }
  }, [state.settingsCompleted]);

  // ==================== EVENT HANDLERS ====================

  // 역할 추가
  const handleAddLabel = () => {
    if (!labelName.trim()) {
      currentSetError(t("SingleRoleRoulette.enterRoleName"));
      return;
    }
    if (labelName.length > MAX_LABEL_LENGTH) {
      currentSetError(
        t("SingleRoleRoulette.roleNameLength", { maxLength: MAX_LABEL_LENGTH })
      );
      return;
    }
    if (state.labels.some((l) => l.name === labelName)) {
      currentSetError(t("SingleRoleRoulette.roleNameExists"));
      return;
    }
    if (labelCount < 1) {
      currentSetError(t("SingleRoleRoulette.quantityMinimum"));
      return;
    }
    dispatch({
      type: "ADD_LABEL",
      label: { name: labelName, count: labelCount, explain: "" },
    });
    setLabelName("");
    setLabelCount(1);
    currentSetError("");
  };

  // 역할 삭제
  const handleRemoveLabel = (name: string) => {
    dispatch({ type: "REMOVE_LABEL", name });
  };

  // default 역할 변경
  const handleDefaultLabelChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDefaultLabel(e.target.value);
    dispatch({
      type: "SET_DEFAULT_LABEL",
      defaultLabel: { name: e.target.value, explain: "" },
    });
  };

  // 총 인원 수 변경
  const handleTotalPlayersChange = (newCount: number) => {
    dispatch({ type: "SET_TOTAL_PLAYERS", count: Math.max(1, newCount) });
  };

  // 게임 모드 변경
  const handleGameModeChange = (mode: GameMode) => {
    dispatch({ type: "SET_GAME_MODE", mode });
  };

  // // 설정 완료 버튼 클릭 시
  // const handleCompleteSettings = () => {
  //   // 1. 기본 역할 검증
  //   if (!state.defaultLabel?.name || !state.defaultLabel.name.trim()) {
  //     setError(t("SingleRoleRoulette.enterBasicRole"));
  //     return;
  //   }

  //   // 2. 총 인원 수 검증
  //   if (state.totalPlayers < 1) {
  //     setError(t("SingleRoleRoulette.setTotalPlayersMinimum"));
  //     return;
  //   }

  //   // 3. 특수역할 인원 수 총합 검증
  //   const totalLabelCount = state.labels.reduce((sum, l) => sum + l.count, 0);
  //   if (totalLabelCount > state.totalPlayers) {
  //     setError(t("SingleRoleRoulette.totalLabelCountExceedsTotalPlayers"));
  //     return;
  //   }

  //   dispatch({ type: "SET_SETTINGS_COMPLETED", completed: true });
  //   setError("");
  // };

  // // 게임 시작 버튼 클릭 시
  // const handleStart = () => {
  //   dispatch({ type: "START_GAME" });
  //   setError("");
  // };

  // // 설정 수정하기 버튼 클릭 시
  // const handleResetSettings = () => {
  //   dispatch({ type: "SET_SETTINGS_COMPLETED", completed: false });
  //   setError("");
  // };

  // // 게임 재시작 버튼
  // const handleRestart = () => {
  //   dispatch({ type: "RESET" });
  //   setError("");
  // };

  // ==================== RENDER FUNCTIONS ====================

  const renderCompletedDefaultLabel = () => (
    <div className="bg-gradient-to-br from-blue-900/30 to-indigo-900/30 border border-blue-700/30 rounded-lg px-3 py-2">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-blue-300">
          {t("RoleDistributor.basic")}
        </span>
        <span className="text-white font-medium">
          {state.defaultLabel.name}
        </span>
      </div>
    </div>
  );

  const renderCompletedSpecialLabels = () => {
    if (state.labels.length === 0) return null;

    return (
      <div className="bg-gradient-to-br from-purple-900/30 to-pink-900/30 border border-purple-700/30 rounded-lg p-3 mt-4">
        <div className="text-sm font-medium text-purple-300 mb-3 flex items-center">
          <i className="fas fa-list mr-2" />
          {t("RoleDistributor.specialRoles")}
        </div>
        <div className="flex flex-wrap gap-2">
          {state.labels.map((label) => (
            <div
              key={label.name}
              className="flex items-center justify-between p-1.5 bg-purple-900/20 border border-purple-700/30 rounded-lg min-w-[100px] mb-2"
            >
              <span className="text-white font-medium text-sm">
                {label.name}
              </span>
              <span className="text-purple-200 text-sm">{label.count}</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderCompletedSettings = () => (
    <div className="bg-gradient-to-br from-green-900/30 to-emerald-900/30 border border-green-700/30 rounded-lg px-3 py-2">
      <div className="text-sm font-medium text-green-300 mb-3">
        {t("RoleDistributor.settingsCompleted")}
      </div>

      {/* 총 인원 표시 */}
      <div className="bg-gray-800/50 border border-gray-600 rounded-lg px-3 py-2 mb-3">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-gray-300">
            {t("SingleRoleRoulette.totalPlayers")}
          </span>
          <span className="text-white font-medium">
            {state.totalPlayers}
            {t("SingleRoleRoulette.people")}
          </span>
        </div>
      </div>

      {/* 게임 모드 표시 */}
      <div className="bg-gray-800/50 border border-gray-600 rounded-lg px-3 py-2 mb-3">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-gray-300">
            {t("SingleRoleRoulette.gameMode")}
          </span>
          <span className="text-purple-300 font-medium text-sm">
            {state.gameMode === GameMode.ROULETTE
              ? t("SingleRoleRoulette.rouletteMode")
              : t("SingleRoleRoulette.moderatorMode")}
          </span>
        </div>
      </div>

      {renderCompletedDefaultLabel()}
      {renderCompletedSpecialLabels()}
    </div>
  );

  const renderTotalPlayersInput = () => (
    <div className="bg-gradient-to-br from-indigo-900/30 to-blue-900/30 border border-indigo-700/30 rounded-lg px-3 py-2">
      <div className="flex items-center gap-2">
        <label className="text-sm font-medium text-indigo-300 whitespace-nowrap">
          {t("SingleRoleRoulette.totalPlayers")}
        </label>
        <div className="flex items-center justify-center gap-2 ml-auto">
          <button
            onClick={() => handleTotalPlayersChange(state.totalPlayers - 1)}
            disabled={state.totalPlayers <= 1}
            className="px-2 py-1 text-indigo-300 hover:text-white hover:bg-indigo-600/20 disabled:opacity-50 disabled:cursor-not-allowed rounded transition-colors text-sm"
          >
            −
          </button>
          <span className="text-white font-medium text-sm min-w-[40px] text-center">
            {state.totalPlayers}
          </span>
          <button
            onClick={() => handleTotalPlayersChange(state.totalPlayers + 1)}
            className="px-2 py-1 text-indigo-300 hover:text-white hover:bg-indigo-600/20 rounded transition-colors text-sm"
          >
            +
          </button>
        </div>
      </div>
    </div>
  );

  const renderGameModeSelector = () => (
    <div className="bg-gradient-to-br from-amber-900/30 to-orange-900/30 border border-amber-700/30 rounded-lg px-3 py-2">
      <div className="text-sm font-medium text-amber-300 mb-3 flex items-center">
        <i className="fas fa-gamepad mr-2" />
        {t("SingleRoleRoulette.gameMode")}
      </div>
      <div className="grid grid-cols-2 gap-2">
        <button
          onClick={() => handleGameModeChange(GameMode.ROULETTE)}
          className={`p-2 rounded-lg border transition-colors text-xs ${
            state.gameMode === GameMode.ROULETTE
              ? "border-amber-500 bg-amber-600/30 text-amber-100"
              : "border-gray-600 bg-gray-800/50 text-gray-300 hover:border-amber-500/50"
          }`}
        >
          <div className="font-medium">
            {t("SingleRoleRoulette.rouletteMode")}
          </div>
          <div className="text-xs mt-1 opacity-80">
            {t("SingleRoleRoulette.rouletteModeDescription")}
          </div>
        </button>
        <button
          onClick={() => handleGameModeChange(GameMode.MODERATOR)}
          className={`p-2 rounded-lg border transition-colors text-xs ${
            state.gameMode === GameMode.MODERATOR
              ? "border-amber-500 bg-amber-600/30 text-amber-100"
              : "border-gray-600 bg-gray-800/50 text-gray-300 hover:border-amber-500/50"
          }`}
        >
          <div className="font-medium">
            {t("SingleRoleRoulette.moderatorMode")}
          </div>
          <div className="text-xs mt-1 opacity-80">
            {t("SingleRoleRoulette.moderatorModeDescription")}
          </div>
        </button>
      </div>
    </div>
  );

  // 기본 역할 입력 렌더링
  const renderDefaultLabelInput = () => (
    <div className="bg-gradient-to-br from-blue-900/30 to-indigo-900/30 border border-blue-700/30 rounded-lg px-3 py-2">
      <div className="flex items-center gap-2">
        <label className="text-sm font-medium text-blue-300 whitespace-nowrap w-16">
          {t("RoleDistributor.basic")}
        </label>
        <input
          type="text"
          value={defaultLabel}
          maxLength={MAX_LABEL_LENGTH}
          onChange={handleDefaultLabelChange}
          placeholder={t("RoleDistributor.basicPlaceholder")}
          className="flex-1 min-w-0 px-2 py-1 bg-gray-800/50 rounded text-white placeholder-gray-500/50 border-blue-500 ring-1 ring-blue-500/30 text-sm"
        />
      </div>
    </div>
  );

  // 수량 조절 버튼 렌더링
  const renderCountControls = () => (
    <div className="flex items-center bg-gray-800/50 rounded border-purple-500 ring-1 ring-purple-500/30">
      <button
        onClick={() => setLabelCount(Math.max(1, labelCount - 1))}
        className="px-2 py-1 text-purple-300 hover:text-white hover:bg-purple-600/20 rounded-l transition-colors text-sm"
      >
        −
      </button>
      {isEditingCount ? (
        <input
          type="number"
          min={1}
          value={labelCount}
          onChange={(e) =>
            setLabelCount(Math.max(1, Number(e.target.value) || 1))
          }
          onBlur={() => setIsEditingCount(false)}
          onKeyDown={(e) => {
            if (e.key === "Enter") setIsEditingCount(false);
          }}
          className="w-12 px-1 py-1 text-center bg-transparent text-white text-sm border-0 outline-none"
          autoFocus
        />
      ) : (
        <button
          onClick={() => setIsEditingCount(true)}
          className="w-12 px-1 py-1 text-center text-white hover:bg-purple-600/20 transition-colors text-sm"
        >
          {labelCount}
        </button>
      )}
      <button
        onClick={() => setLabelCount(labelCount + 1)}
        className="px-2 py-1 text-purple-300 hover:text-white hover:bg-purple-600/20 rounded-r transition-colors text-sm"
      >
        +
      </button>
    </div>
  );

  // 특수 역할 추가 영역 렌더링
  const renderSpecialLabelInput = () => (
    <div className="bg-gradient-to-br from-purple-900/30 to-pink-900/30 border border-purple-700/30 rounded-lg px-3 py-2">
      <div className="flex flex-col gap-3">
        {/* 입력 필드 */}
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-purple-300 whitespace-nowrap w-16">
            {t("RoleDistributor.special")}
          </span>
          <input
            type="text"
            placeholder={t("RoleDistributor.specialPlaceholder")}
            value={labelName}
            maxLength={MAX_LABEL_LENGTH}
            onChange={(e) => {
              setLabelName(e.target.value);
              // 사용자가 특수 역할을 입력하면 에러 클리어
              if (currentError && e.target.value.trim()) {
                currentSetError("");
              }
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleAddLabel();
            }}
            className="flex-1 min-w-0 px-2 py-1 bg-gray-800/50 rounded text-white placeholder-gray-500/50 border-purple-500 ring-1 ring-purple-500/30 text-sm"
          />
        </div>

        {/* 수량 조절과 추가 버튼 */}
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2 flex-1">
            <div className="text-purple-300 text-sm font-medium whitespace-nowrap w-16">
              {t("RoleDistributor.quantity")}
            </div>
            {renderCountControls()}
          </div>
          <button
            onClick={handleAddLabel}
            className="flex-1 py-1 bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-700 hover:to-green-700 text-white rounded-lg font-medium transition-colors text-sm flex items-center justify-center gap-2"
            disabled={!labelName.trim()}
          >
            <i className="fas fa-plus-circle text-sm" />
            <span>{t("LuckyDraw.add")}</span>
          </button>
        </div>
      </div>
    </div>
  );

  // 개별 역할 아이템 렌더링
  const renderLabelItem = (label: { name: string; count: number }) => (
    <div
      key={label.name}
      className="flex items-center justify-between p-2 bg-emerald-900/20 border border-emerald-700/30 rounded-lg"
    >
      <div className="flex items-center ml-2 gap-4 flex-1 min-w-0">
        <div className="flex items-center gap-1 text-blue-300 flex-shrink-0">
          <i className="fas fa-users text-xs" />
          <span className="text-sm font-medium">{label.count}</span>
        </div>
        <span className="text-white font-medium text-sm truncate">
          {label.name}
        </span>
      </div>
      <button
        onClick={() => handleRemoveLabel(label.name)}
        className="px-2 py-1 bg-red-600/80 hover:bg-red-600 text-white rounded text-xs transition-colors flex items-center flex-shrink-0"
      >
        <i className="fas fa-trash mr-1 text-xs" />
        {t("RoleDistributor.delete")}
      </button>
    </div>
  );

  // 역할 목록 렌더링
  const renderLabelsList = () => {
    if (state.labels.length === 0) return null;

    return (
      <div className="bg-gradient-to-br from-emerald-900/30 to-teal-900/30 border border-emerald-700/30 rounded-lg p-4">
        <h4 className="text-sm font-medium text-emerald-300 mb-4 flex items-center">
          <i className="fas fa-list mr-2" />
          {t("RoleDistributor.addedSpecialRoles")}
        </h4>
        <div className="grid gap-1.5">{state.labels.map(renderLabelItem)}</div>
      </div>
    );
  };

  const renderSettingsInput = () => (
    <div className="space-y-4">
      {renderTotalPlayersInput()}
      {renderGameModeSelector()}
      {renderDefaultLabelInput()}
      {renderSpecialLabelInput()}
      {renderLabelsList()}
    </div>
  );

  const renderErrorMessage = () => {
    if (!currentError) return null;
    return (
      <div className="p-3 bg-red-900/50 border border-red-600/50 rounded-lg text-red-200 text-sm flex items-center gap-2">
        <i className="fas fa-exclamation-triangle text-red-400" />
        {currentError}
      </div>
    );
  };

  // ==================== MAIN RENDER ====================

  return (
    <div className="space-y-4">
      {renderErrorMessage()}

      <div className={state.settingsCompleted ? "hidden" : ""}>
        {renderSettingsInput()}
      </div>

      <div className={!state.settingsCompleted ? "hidden" : ""}>
        {renderCompletedSettings()}
      </div>
    </div>
  );
};

export default SingleRoleRouletteGameSetting;
